import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Plus } from "lucide-react";

export default function GRN() {
  const [items, setItems] = useState([]);
  const [pos, setPos] = useState([]);
  const [open, setOpen] = useState(false);
  const [selectedPo, setSelectedPo] = useState(null);
  const [lines, setLines] = useState([]);
  const [notes, setNotes] = useState("");

  const load = async () => {
    const [g, p] = await Promise.all([
      api.get("/procurement/grn"),
      api.get("/procurement/purchase-orders"),
    ]);
    setItems(g.data);
    setPos(p.data.filter((po) => ["APPROVED", "ACKNOWLEDGED", "PARTIALLY_RECEIVED"].includes(po.status)));
  };
  useEffect(() => { load(); }, []);

  const pickPo = (poId) => {
    const po = pos.find((p) => p.id === poId);
    setSelectedPo(po);
    if (po) setLines(po.lines.map((l, idx) => ({
      po_line_index: idx, item_name: l.item_name,
      received_quantity: l.quantity, accepted_quantity: l.quantity, rejected_quantity: 0, remarks: "",
    })));
  };

  const submit = async () => {
    if (!selectedPo) return;
    try {
      await api.post("/procurement/grn", {
        purchase_order_id: selectedPo.id,
        received_date: new Date().toISOString(),
        notes,
        lines: lines.map((l) => ({
          po_line_index: l.po_line_index,
          item_name: l.item_name,
          received_quantity: Number(l.received_quantity),
          accepted_quantity: Number(l.accepted_quantity),
          rejected_quantity: Number(l.rejected_quantity),
          remarks: l.remarks,
        })),
      });
      toast.success("GRN posted");
      setOpen(false); setSelectedPo(null); setLines([]); setNotes("");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const updateLine = (idx, k, v) => {
    const next = [...lines]; next[idx] = { ...next[idx], [k]: v }; setLines(next);
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-4 sm:space-y-6" data-testid="grn-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-black">Goods Receipt Notes</h1>
          <p className="text-muted-foreground">Record what arrived against a PO. Updates PO fulfillment status automatically.</p>
        </div>
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setSelectedPo(null); setLines([]); setNotes(""); } }}>
          <DialogTrigger asChild><Button data-testid="new-grn-btn"><Plus className="w-4 h-4 mr-1" /> New GRN</Button></DialogTrigger>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="grn-dialog">
            <DialogHeader><DialogTitle>Post a Goods Receipt Note</DialogTitle></DialogHeader>
            <div className="space-y-3">
              {pos.length === 0 ? (
                <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                  No approved Purchase Orders available. Create and approve a PO first under <b>Purchase orders</b>.
                </div>
              ) : null}
              <div className="space-y-2"><Label>Purchase Order</Label>
                <Select value={selectedPo?.id || ""} onValueChange={pickPo}>
                  <SelectTrigger data-testid="grn-po-select"><SelectValue placeholder="Pick an approved PO" /></SelectTrigger>
                  <SelectContent>
                    {pos.map((p) => <SelectItem key={p.id} value={p.id}>{p.number} · {p.vendor_name} · ₹{Number(p.total || 0).toLocaleString()}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              {selectedPo && (
                <>
                  <div className="border border-border rounded-md">
                    <div className="px-3 py-2 bg-muted/50 label-overline">Receipt lines</div>
                    <Table>
                      <TableHeader><TableRow><TableHead>Item</TableHead><TableHead>Ordered</TableHead><TableHead>Received</TableHead><TableHead>Accepted</TableHead><TableHead>Rejected</TableHead></TableRow></TableHeader>
                      <TableBody>
                        {lines.map((l, idx) => (
                          <TableRow key={idx}>
                            <TableCell>{l.item_name}</TableCell>
                            <TableCell className="font-mono text-xs">{selectedPo.lines[l.po_line_index].quantity}</TableCell>
                            <TableCell className="w-24"><Input type="number" value={l.received_quantity} onChange={(e) => updateLine(idx, "received_quantity", e.target.value)} data-testid={`grn-recv-${idx}`} /></TableCell>
                            <TableCell className="w-24"><Input type="number" value={l.accepted_quantity} onChange={(e) => updateLine(idx, "accepted_quantity", e.target.value)} data-testid={`grn-acc-${idx}`} /></TableCell>
                            <TableCell className="w-24"><Input type="number" value={l.rejected_quantity} onChange={(e) => updateLine(idx, "rejected_quantity", e.target.value)} /></TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                  <Input placeholder="Notes" value={notes} onChange={(e) => setNotes(e.target.value)} />
                  <Button onClick={submit} data-testid="grn-post-btn">Post GRN</Button>
                </>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="surface-card p-4 overflow-x-auto">
        <Table>
          <TableHeader><TableRow><TableHead>Number</TableHead><TableHead>PO</TableHead><TableHead>Vendor</TableHead><TableHead>Date</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
          <TableBody>
            {items.map((i) => (
              <TableRow key={i.id} data-testid={`grn-row-${i.id}`}>
                <TableCell className="font-mono text-xs">{i.number}</TableCell>
                <TableCell className="font-mono text-xs">{i.po_number}</TableCell>
                <TableCell>{i.vendor_name}</TableCell>
                <TableCell className="text-xs">{new Date(i.received_date || i.created_at).toLocaleDateString()}</TableCell>
                <TableCell>{i.status}</TableCell>
              </TableRow>
            ))}
            {items.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-8">No GRNs yet.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
